import { bytesToHex, encodeAbiParameters, hexToBytes, keccak256, stringToHex, } from "viem";
export const NETLIST_GATE_COUNT = 5;
export const NETLIST_RECORD_BYTES = 7;
export const NETLIST_BYTES = NETLIST_GATE_COUNT * NETLIST_RECORD_BYTES;
export const NETLIST_FORMAT_DOMAIN = keccak256(stringToHex("TAPEOUT_NAND_NETLIST_V2_FIXED5"));
export const TAPEOUT_COMMIT_TYPEHASH = keccak256(stringToHex("TapeoutCommit(bytes32 formatDomain,uint256 chainId,address projectCore,address designer,address receiver,bytes32 sortedNandIdsHash,bytes32 netlistHash,bytes32 userNonce,bytes32 salt)"));
export class NetlistError extends Error {
    code;
    gateIndex;
    name = "NetlistError";
    constructor(code, message, gateIndex) {
        super(message);
        this.code = code;
        this.gateIndex = gateIndex;
    }
}
function bytesFrom(value) {
    if (value instanceof Uint8Array)
        return Uint8Array.from(value);
    try {
        return hexToBytes(value);
    }
    catch {
        throw new NetlistError("INVALID_LENGTH", "Netlist must be valid even-length hex bytes");
    }
}
function decodeU24(bytes, offset) {
    return ((bytes[offset] ?? 0) << 16) | ((bytes[offset + 1] ?? 0) << 8) | (bytes[offset + 2] ?? 0);
}
function computeReachability(gates) {
    let mask = 1 << (NETLIST_GATE_COUNT - 1);
    for (let cursor = NETLIST_GATE_COUNT; cursor > 0; cursor -= 1) {
        const gateIndex = cursor - 1;
        if ((mask & (1 << gateIndex)) === 0)
            continue;
        const gate = gates[gateIndex];
        if (gate === undefined)
            throw new NetlistError("INVALID_LENGTH", "Missing gate");
        if (gate.lhs >= 4)
            mask |= 1 << (gate.lhs - 4);
        if (gate.rhs >= 4)
            mask |= 1 << (gate.rhs - 4);
    }
    return mask;
}
export function parseNetlist(value) {
    const bytes = bytesFrom(value);
    if (bytes.length !== NETLIST_BYTES) {
        throw new NetlistError("INVALID_LENGTH", `Netlist must be exactly ${NETLIST_BYTES} bytes; received ${bytes.length}`);
    }
    const gates = [];
    for (let gateIndex = 0; gateIndex < NETLIST_GATE_COUNT; gateIndex += 1) {
        const offset = gateIndex * NETLIST_RECORD_BYTES;
        const opcode = bytes[offset];
        if (opcode !== 0) {
            throw new NetlistError("INVALID_OPCODE", `Gate ${gateIndex} uses unsupported opcode ${String(opcode)}`, gateIndex);
        }
        const lhs = decodeU24(bytes, offset + 1);
        const rhs = decodeU24(bytes, offset + 4);
        const maximumReference = 3 + gateIndex;
        if (lhs > maximumReference || rhs > maximumReference) {
            throw new NetlistError("INVALID_REFERENCE", `Gate ${gateIndex} may only reference signals 0..${maximumReference}`, gateIndex);
        }
        gates.push({ lhs, rhs });
    }
    const reachabilityMask = computeReachability(gates);
    for (let gateIndex = 0; gateIndex < NETLIST_GATE_COUNT; gateIndex += 1) {
        if ((reachabilityMask & (1 << gateIndex)) === 0) {
            throw new NetlistError("UNREACHABLE_GATE", `Gate ${gateIndex} is not reachable from the fifth gate output`, gateIndex);
        }
    }
    return {
        gates: gates,
        reachabilityMask,
        canonicalHex: bytesToHex(bytes),
    };
}
function encodeU24(value, output, offset) {
    if (!Number.isSafeInteger(value) || value < 0 || value > 0xffffff) {
        throw new NetlistError("INVALID_REFERENCE", `Signal ${value} is outside the u24 range`);
    }
    output[offset] = (value >>> 16) & 0xff;
    output[offset + 1] = (value >>> 8) & 0xff;
    output[offset + 2] = value & 0xff;
}
export function encodeNetlist(gates) {
    const bytes = new Uint8Array(NETLIST_BYTES);
    gates.forEach((gate, gateIndex) => {
        const offset = gateIndex * NETLIST_RECORD_BYTES;
        bytes[offset] = 0;
        encodeU24(gate.lhs, bytes, offset + 1);
        encodeU24(gate.rhs, bytes, offset + 4);
    });
    return parseNetlist(bytes).canonicalHex;
}
export function evaluateNetlist(value, a, b) {
    const { gates } = parseNetlist(value);
    const signals = [false, true, a, b];
    for (const gate of gates) {
        const lhs = signals[gate.lhs];
        const rhs = signals[gate.rhs];
        if (lhs === undefined || rhs === undefined) {
            throw new NetlistError("INVALID_REFERENCE", "Evaluator encountered an unknown signal");
        }
        signals.push(!(lhs && rhs));
    }
    return signals[8] ?? false;
}
export function simulateAllCases(value) {
    return [
        evaluateNetlist(value, false, false),
        evaluateNetlist(value, true, false),
        evaluateNetlist(value, false, true),
        evaluateNetlist(value, true, true),
    ];
}
export function implementsNand(value) {
    const outputs = simulateAllCases(value);
    return outputs[0] && outputs[1] && outputs[2] && !outputs[3];
}
export function canonicalizeNetlist(value) {
    return parseNetlist(value).canonicalHex;
}
export function netlistHash(value) {
    return keccak256(canonicalizeNetlist(value));
}
export function computeTapeoutCommitment(input) {
    const sortedNandIdsHash = keccak256(encodeAbiParameters([{ type: "uint256[5]" }], [input.sortedNandIds]));
    return keccak256(encodeAbiParameters([
        { type: "bytes32" },
        { type: "bytes32" },
        { type: "uint256" },
        { type: "address" },
        { type: "address" },
        { type: "address" },
        { type: "bytes32" },
        { type: "bytes32" },
        { type: "bytes32" },
        { type: "bytes32" },
    ], [
        TAPEOUT_COMMIT_TYPEHASH,
        NETLIST_FORMAT_DOMAIN,
        input.chainId,
        input.projectCore,
        input.designer,
        input.receiver,
        sortedNandIdsHash,
        input.netlistHash,
        input.userNonce,
        input.salt,
    ]));
}
//# sourceMappingURL=index.js.map